import { useEffect, useEffectEvent } from 'react';

import { useAccessToken } from '@mint-lib/auth';
import type { Auth } from '@mint-sdk/api-auth';
import {
  BULK_TASKS_SOCKET_PATH,
  bulkTasksSocket,
  onBulkTaskUpdate,
  subscribeBulkTask,
} from '@mint-sdk/api-auth/socket';
import { io } from 'socket.io-client';

type Args = {
  taskId: string | null;
  onUpdate: (status: Auth.BulkTaskStatus) => void;
  onError: () => void;
};

const port = globalThis.location.port;
const isSecure = port === '' || port === '443';
const protocol = isSecure ? 'wss' : 'ws';
const portSuffix = port === '443' ? '' : `:${port}`;
const WS_URL = `${protocol}://${globalThis.location.hostname}${portSuffix}`;

const useBulkTaskSocket = ({ taskId, onUpdate, onError }: Args) => {
  const accessToken = useAccessToken();
  const handleUpdate = useEffectEvent(onUpdate);
  const handleError = useEffectEvent(onError);

  useEffect(() => {
    if (!taskId || !accessToken) return;

    const rawSocket = io(WS_URL, {
      path: BULK_TASKS_SOCKET_PATH,
      // for testing and debuging can be
      // transports: ['polling'],
      transports: ['websocket'],
      // Sent inside the Socket.IO CONNECT packet, where the BE reads it.
      query: { authorization: encodeURIComponent(accessToken) },
    });
    bulkTasksSocket.provideClient(rawSocket);

    const unsubscribe = onBulkTaskUpdate((payload) => handleUpdate(payload));

    const handleConnect = () => subscribeBulkTask({ taskId });
    rawSocket.on('connect', handleConnect);
    if (rawSocket.connected) handleConnect();
    rawSocket.on('connect_error', () => {
      if (!rawSocket.active) handleError();
    });

    return () => {
      unsubscribe();
      rawSocket.off('connect', handleConnect);
      rawSocket.disconnect();
    };
  }, [taskId, accessToken]);
};

export default useBulkTaskSocket;
